import { useState, useEffect } from "react";
import { Modal, Stack, Text } from "@mantine/core";
import { Button } from "@xplatform/design-system";
import { IconPlus } from "@tabler/icons-react";
import type { ModelViewModel } from "@models/model";
import { providerApi } from "@services/providerApi";
import { ModelCard } from "./ModelCard";
import { ModelForm } from "./ModelForm";
import {
    emptyForm,
    extractTiers,
    tiersToFormTiers,
    metadataToRecord,
} from "../../utils/modelModalHelper";
import type { FormState } from "../../utils/modelModalHelper";

interface ModelModalProps {
    opened: boolean;
    onClose: () => void;
    providerId: string;
    providerKey: string;
    models: ModelViewModel[];
    onRefresh: () => void;
}

export const ModelModal = ({
    opened,
    onClose,
    providerId,
    providerKey,
    models,
    onRefresh,
}: ModelModalProps) => {
    const [form, setForm] = useState<FormState>(emptyForm());
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    useEffect(() => {
        if (!opened) {
            setShowForm(false);
            setEditingId(null);
            setForm(emptyForm());
            setError(null);
        }
    }, [opened]);

    const cancelForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm(emptyForm());
        setError(null);
    };

    const startEdit = (m: ModelViewModel) => {
        setEditingId(m.id);
        const cfg = m.costInfo?.costConfig ?? {};
        setForm({
            modelName: m.modelName,
            displayName: m.displayName ?? "",
            status: m.status,
            metadata: Object.entries(m.modelMetadata ?? {}).map(([key, value]) => ({
                key,
                value: String(value),
            })),
            inputTiers: tiersToFormTiers(extractTiers(cfg, "input_tokens")),
            outputTiers: tiersToFormTiers(extractTiers(cfg, "output_tokens")),
            currency: m.costInfo?.currency ?? "USD",
        });
        setShowForm(true);
        setError(null);
    };

    const buildCostInfo = (f: FormState) => {
        const toEntry = (t: FormState["inputTiers"][number]) => ({
            unitCost: Number(t.price),
            unitName: "token",
            unitScale: 1000000,
            tierStart: t.tierStart !== "" ? Number(t.tierStart) : null,
            tierEnd: t.tierEnd !== "" ? Number(t.tierEnd) : null,
        });
        const validInput = f.inputTiers.filter((t) => t.price !== "");
        const validOutput = f.outputTiers.filter((t) => t.price !== "");
        if (!validInput.length && !validOutput.length) return undefined;

        const costConfig: Record<string, ReturnType<typeof toEntry>> = {};
        if (validInput.length === 1) costConfig["input_tokens"] = toEntry(validInput[0]);
        else validInput.forEach((t, i) => (costConfig[`input_tokens_${i}`] = toEntry(t)));
        if (validOutput.length === 1) costConfig["output_tokens"] = toEntry(validOutput[0]);
        else validOutput.forEach((t, i) => (costConfig[`output_tokens_${i}`] = toEntry(t)));

        return { currency: f.currency, effectiveFrom: new Date().toISOString().split("T")[0], costConfig };
    };

    const handleSubmit = async () => {
        if (!form.modelName.trim()) { setError("Model name is required"); return; }
        setError(null);
        setLoading(true);
        try {
            const metadata = metadataToRecord(form.metadata);
            const costInfo = buildCostInfo(form);
            const meta = Object.keys(metadata).length ? metadata : undefined;
            if (editingId) {
                await providerApi.updateModel(editingId, {
                    displayName: form.displayName.trim() || undefined,
                    status: form.status,
                    modelMetadata: meta,
                    costInfo,
                });
            } else {
                await providerApi.createModel(providerId, {
                    providerId,
                    modelName: form.modelName.trim(),
                    displayName: form.displayName.trim() || undefined,
                    status: form.status,
                    modelMetadata: meta,
                    costInfo,
                });
            }
            onRefresh();
            cancelForm();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to save model");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (modelId: string) => {
        setDeletingId(modelId);
        try {
            await providerApi.deleteModel(modelId);
            onRefresh();
        } catch (err) {
            console.error(err);
        } finally {
            setDeletingId(null);
        }
    };

    const handleToggleStatus = async (m: ModelViewModel) => {
        const newStatus = m.status === "active" ? "disabled" : "active";
        try {
            await providerApi.updateModel(m.id, { status: newStatus });
            onRefresh();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Text fw={600}>Models - {providerKey}</Text>}
            size="xl"
            portalProps={{ target: "#admin-portal-root" }}
        >
            <Stack gap="md">
                {models.length === 0 && !showForm && (
                    <Text c="dimmed" size="sm" ta="center" py="lg">
                        No models configured
                    </Text>
                )}

                {models.length > 0 && (
                    <Stack gap="xs">
                        {models.map((m) => (
                            <ModelCard
                                key={m.id}
                                model={m}
                                deletingId={deletingId}
                                onEdit={startEdit}
                                onDelete={handleDelete}
                                onToggleStatus={handleToggleStatus}
                            />
                        ))}
                    </Stack>
                )}

                {!showForm && (
                    <Button
                        leftSection={<IconPlus size={14} />}
                        variant="light"
                        onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm()); }}
                    >
                        Add Model
                    </Button>
                )}

                {showForm && (
                    <ModelForm
                        form={form}
                        setForm={setForm}
                        editingId={editingId}
                        error={error}
                        loading={loading}
                        onCancel={cancelForm}
                        onSubmit={handleSubmit}
                    />
                )}
            </Stack>
        </Modal>
    );
};
